<template>
    <div id="print-queue-container">
        <card title="Call Log">
            <template #header-right>
                <b-button v-if="canManageQuestionSets" variant="secondary" size="sm" @click="showQuestionSetsModal = true">
                    <i class="fas fa-cog"></i> Manage Audit Forms
                </b-button>
            </template>
            <b-form class="filter-grid mb-3" @submit.prevent="searchCalls">
                <b-row>
                    <b-col>
                        <SearchComponent v-model="userFilter" label="Agent" :option-label="userLabel"
                            search-u-r-l="/api/Call/GetAllAgents" prepopulate clearable />
                    </b-col>
                    <b-col>
                        <fg-input v-model="externalNumber" label="External Number:" />
                    </b-col>
                    <b-col>
                        <fg-input v-model="recordingSidFilter" label="Recording SID:" />
                    </b-col>
                    <b-col>
                        <SearchComponent v-model="patientFilter" label="Patient" :option-label="patientLabel"
                            search-u-r-l="/Patient" clearable />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>

                        <div>
                            <label>Call Date:</label>
                            <div class="d-flex" style="gap: 15px;">
                                <el-date-picker size="sm" v-model="startDate" today-button reset-button
                                    format="MM/dd/yyyy" type="date" />
                                <el-date-picker size="sm" v-model="endDate" today-button reset-button
                                    format="MM/dd/yyyy" type="date" />
                            </div>
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button type="submit" variant="primary">Search</b-button>
                    </b-col>
                </b-row>
            </b-form>
        </card>
        <card>
            <b-table id="CallLog" striped responsive="true" sort-icon-left :items="calls" :fields="fields">
                <template #cell(expandToggle)="data">
                    <div @click="selectRecording(data)" style="cursor: pointer; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;">
                        <i :class="{ 'fa fa-fw fa-chevron-right text-primary': true, 'fa-rotate-90': data.detailsShowing }"
                            style="transition: transform .15s;"></i>
                    </div>
                </template>
                <template #cell(patient)="data">
                    <router-link v-if="data.item.patientId && (data.item.patientFirstName || data.item.patientLastName)"
                        :to="`/processing/patient-dashboard/${data.item.patientId}`">
                        {{ data.item.patientFirstName }} {{ data.item.patientLastName }}
                    </router-link>
                </template>
                <template #cell(auditActions)="data">
                    <div class="d-flex gap-2">
                        <b-button v-if="canCompleteAudits && !data.item.auditCompletedAt"
                            variant="success" size="sm" @click="startAudit(data.item)">
                            <i class="fas fa-clipboard-check"></i> Audit
                        </b-button>
                        <b-button v-if="canViewAudits && data.item.auditCompletedAt"
                            variant="info" size="sm" @click="viewAudit(data.item)">
                            <i class="fas fa-eye"></i> View Audit
                        </b-button>
                    </div>
                </template>
                <template #row-details="row">
                    <card plain class="bg-white">
                        <div class="mb-3">
                            <strong>Recording SID:</strong> {{ row.item.recordingSid }}
                        </div>
                        <div v-if="row.item.dispositions || row.item.wrapUpNotes" class="mb-3">
                            <div v-if="row.item.dispositions" class="mb-2">
                                <strong>Disposition:</strong> {{ row.item.dispositions }}
                            </div>
                            <div v-if="row.item.wrapUpNotes">
                                <strong>Wrap-up Notes:</strong>
                                <p class="mt-1 mb-0" style="white-space: pre-wrap;">{{ row.item.wrapUpNotes }}</p>
                            </div>
                        </div>
                        <CallRecordingPlayer :recording-sid="row.item.recordingSid" :call-id="row.item.id"
                            :transcript-compact="transcriptCompactMode" />
                    </card>
                </template>
            </b-table>

            <BottomPagerBar hide-pager no-stick item-name="call" :total-rows="calls.length" show-total>
                <template #left>
                    <b-dropdown
                        :text="transcriptCompactMode ? 'Compact Transcript View' : 'Expanded Transcript View'"
                        variant="primary"
                        size="sm"
                        dropup>
                        <b-dropdown-item
                            :active="!transcriptCompactMode"
                            @click="setTranscriptViewMode(false)">
                            <i class="fas fa-list mr-2"></i>
                            Expanded View (with timestamps)
                        </b-dropdown-item>
                        <b-dropdown-item
                            :active="transcriptCompactMode"
                            @click="setTranscriptViewMode(true)">
                            <i class="fas fa-align-left mr-2"></i>
                            Compact View (text only)
                        </b-dropdown-item>
                    </b-dropdown>
                </template>
            </BottomPagerBar>
        </card>

        <!-- Audit Modal -->
        <CallLogAuditModal v-model="showAuditModal" :call="selectedCall" @audit-submitted="onAuditSubmitted" />

        <!-- Audit Viewer Modal -->
        <CallLogAuditViewer v-model="showAuditViewerModal" :call="selectedCall" />

        <!-- Question Sets Management Modal -->
        <b-modal v-model="showQuestionSetsModal" title="Manage Call Log Audit Forms" size="xl" scrollable hide-footer>
            <TaskQuestionSets :entity-type="callLogEntityType" />
        </b-modal>

        <!-- Deep-linked Call Detail Modal -->
        <b-modal id="deep-link-call-modal" size="lg" scrollable hide-footer centered
            :title="deepLinkedCall ? `${deepLinkedCall.outbound ? 'Outbound' : 'Inbound'} Call — ${deepLinkedCall.externalNumber}` : 'Call Details'">
            <template v-if="deepLinkedCall">
                <div class="mb-2">
                    <strong>Agent:</strong> {{ deepLinkedCall.agent }}
                    <span class="ml-3"><strong>Duration:</strong> {{ deepLinkedCall.duration | formatDuration }}</span>
                    <span class="ml-3"><strong>Initiated:</strong> {{ deepLinkedCall.initiated | formatDate("L LT") }}</span>
                    <span class="ml-3"><strong>Direction:</strong> {{ deepLinkedCall.outbound ? 'Outbound' : 'Inbound' }}</span>
                </div>
                <div class="mb-3">
                    <strong>Recording SID:</strong> {{ deepLinkedCall.recordingSid }}
                </div>
                <CallRecordingPlayer :key="deepLinkedCall.recordingSid" :recording-sid="deepLinkedCall.recordingSid"
                    :call-id="deepLinkedCall.id" :transcript-compact="transcriptCompactMode" />
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts">
import {Component, Vue} from 'vue-property-decorator';
import moment from 'moment';
import SearchComponent from '@/components/SearchComponent.vue';
import {DatePicker} from "element-ui";
import Axios from "axios";
import {Call} from '@/models/Call';
import {Patient} from '@/models/Patient';
import CallRecordingPlayer from '@/components/CallLog/CallRecordingPlayer.vue';
import {NotificationOptions} from '@/util/NotificationOptionsPresets';
import CallLogAuditModal from '@/components/CallLog/CallLogAuditModal.vue';
import CallLogAuditViewer from '@/components/CallLog/CallLogAuditViewer.vue';
import TaskQuestionSets from '@/components/Tasks/TaskQuestionSets.vue';
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { EntityType } from '@/models/EntityType';
import { getUserSetting, saveUserSetting } from "@/services/UserSettingsService";
import { formatDuration } from '@/util/DurationFormatter';

@Component({
    name: "CallLog",
    components: {
        SearchComponent,
        [DatePicker.name]: DatePicker,
        CallRecordingPlayer,
        CallLogAuditModal,
        CallLogAuditViewer,
        TaskQuestionSets,
        BottomPagerBar,
    }
})
export default class CallLogPage extends Vue {
    protected startDate?: string = moment().add(-30, 'day').toISOString();
    protected endDate?: string = moment().toISOString();
    protected externalNumber: string = "";
    protected recordingSidFilter: string = "";
    protected patientFilter: Patient | null = null;
    protected isLoading?: boolean = false;
    protected userFilter: Call = new Call();
    protected calls: Array<Call> = [];
    protected fields: any[] = [
        { key: "expandToggle", label: "" },
        { key: "recordingSid", label: "Recording SID", sortable: true },
        { key: "externalNumber", label: "External Number", sortable: true },
        { key: "agent", label: "Agent", sortable: true },
        { key: "patient", label: "Patient", sortable: true },
        { key: "initiated", label: "Initiated", sortable: true, formatter: (d: Date) => moment.utc(d).local().format("L LT") },
        { key: "duration", label: "Duration", sortable: true, formatter: (s: number) => formatDuration(s) },
        { key: "outbound", label: "Direction", sortable: true, formatter: (d: boolean) => d ? "Outbound" : "Inbound" },
        { key: "dispositions", label: "Disposition", sortable: true },
        { key: "auditActions", label: "Audit", class: "audit-actions" },
    ];

    // Audit functionality properties
    protected selectedCall: Call | null = null;
    protected showAuditModal = false;
    protected showAuditViewerModal = false;
    protected showQuestionSetsModal = false;
    protected callLogEntityType = EntityType.CallLog;
    protected transcriptCompactMode: boolean = false;

    userLabel(c:any) {
        return c.agent ? `${c.agent}` : '';
    }

    patientLabel(p:any) {
        return p.id ? `ID: ${ p.id } ${ p.lastName }, ${ p.firstName }` : ''
    }

    mounted() {
        const savedMode = getUserSetting('transcriptViewMode');
        this.transcriptCompactMode = savedMode === 'compact';

        const callId = this.$route.params.callId;
        if (callId) {
            this.deepLinkToCall(parseInt(callId, 10));
        }

        const phone = this.$route.query["phone"] as string;
        if (phone) {
            this.externalNumber = phone.replace("+", "").trim();
            this.searchCalls();
        }
    }

    protected deepLinkedCall: Call | null = null;

    private async deepLinkToCall(callId: number) {
        try {
            const response = await Axios.get<Call>(`/api/Call/${callId}`);
            this.deepLinkedCall = response.data;
            this.$nextTick(() => this.$bvModal.show('deep-link-call-modal'));
        } catch (err) {
            console.error('Failed to load call', err);
            this.$notification(NotificationOptions.error('Failed to load call details'));
        }
    }

    async searchCalls() {
        try {
            const response = await Axios.get('/api/Call', {
                params: {
                    startDate: this.startDate,
                    endDate: this.endDate,
                    externalNumber: this.externalNumber,
                    agent: this.userFilter?.agent,
                    patientId: this.patientFilter?.id || null,
                    recordingSid: this.recordingSidFilter || null
                }
            });
            this.calls = response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    setTranscriptViewMode(compact: boolean) {
        this.transcriptCompactMode = compact;
        saveUserSetting('transcriptViewMode', compact ? 'compact' : 'expanded');
    }

    selectRecording(r: any) {
        // The player component loads its own audio on mount when the row expands.
        r.toggleDetails();
    }

    // Computed properties for permissions
    get canManageQuestionSets(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === 'Admin/Call Log Question Sets');
    }

    get canCompleteAudits(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === 'Reports/Call Log Audits');
    }

    get canViewAudits(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === 'Reports/View Call Log Audits');
    }

    // Audit functionality methods

    startAudit(call: Call) {
        this.selectedCall = call;
        this.showAuditModal = true;
    }

    viewAudit(call: Call) {
        this.selectedCall = call;
        this.showAuditViewerModal = true;
    }

    async onAuditSubmitted(callId: number) {
        // Update the call object with audit completion
        const call = this.calls.find(c => c.id === callId);
        if (call) {
            call.auditCompletedAt = new Date();
            this.$forceUpdate(); // Force reactivity update
        }
    }

}
</script>

<style scoped>
::v-deep(.audit-actions) {
    width: 150px;
    text-align: center;
}

.d-flex.gap-2 {
    gap: 0.5rem;
}

/* Active dropdown item styling */
::v-deep .dropdown-menu .dropdown-item.active,
::v-deep .dropdown-menu .dropdown-item.active:hover,
::v-deep .dropdown-menu .dropdown-item.active:focus {
    background-color: #007bff !important;
    color: white !important;
    font-weight: bold !important;
}

::v-deep .dropdown-menu .dropdown-item.active i,
::v-deep .dropdown-menu .dropdown-item.active:hover i,
::v-deep .dropdown-menu .dropdown-item.active:focus i {
    color: white !important;
}

::v-deep .dropdown-menu .dropdown-item.active *,
::v-deep .dropdown-menu .dropdown-item.active:hover *,
::v-deep .dropdown-menu .dropdown-item.active:focus * {
    color: white !important;
}
</style>
